/**
 * Player Grid
 *
 * Flat grid of players used before teams are assigned
 * and in FFA (team size 1) scenarios.
 */

import type { FlowPlayer, QueueRankConfig } from "@rivaled/shared";
import { motion } from "framer-motion";
import { PlayerCard } from "../components/PlayerCard";
import { useCurrentPlayerId } from "../hooks/useCurrentPlayerId";
import { getPartyColor } from "../lib/party-colors";
import type { PlayerStatusFn } from "./getNodePlayerStatus";

type PlayerGridProps = {
  players: FlowPlayer[];
  myPartyId: string | null;
  rankConfig: QueueRankConfig | null;
  getPlayerStatus?: PlayerStatusFn;
};

export function PlayerGrid({ players, myPartyId, rankConfig, getPlayerStatus }: PlayerGridProps) {
  const currentPlayerId = useCurrentPlayerId();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
      className="bg-surface-800/60 rounded-lg border border-surface-700/30 overflow-hidden"
    >
      <div className="px-4 pt-3 pb-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-surface-500">Players</h3>
      </div>
      <div className="grid grid-cols-2 @sm:grid-cols-3 @md:grid-cols-4 gap-1.5 p-2">
        {players.map((player, i) => {
          const status = getPlayerStatus?.(player.userId);
          return (
            <PlayerCard
              key={player.userId}
              player={player}
              variant="compact"
              isMe={player.userId === currentPlayerId}
              partyColor={getPartyColor(player.partyId, myPartyId)}
              rankConfig={rankConfig}
              animationDelay={i * 0.03}
              rightSlot={status?.rightSlot}
              className={status?.className}
            />
          );
        })}
      </div>
    </motion.div>
  );
}
